import React from 'react';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';

import Modal from '../../../components/modal/Modal';
import Button from '../../../../components/form/button/Button';
import { Color } from '../../../../core/enums/Color';
import Input from '../../../../components/form/input/Input';
import { GlobalNotificationTypes } from '../../../../core/enums/GlobalNotificationTypes';
import { RootState } from '../../../../core/state/state';
import { spawnGlobalNotification } from '../../../../core/state/global-notifications/asyncActions';
import { getSubcontractorNotificationEmailSettings, saveSubcontractorNotificationEmail } from '../../../../core/state/subcontractors/asyncActions';

import './EditNotificationsMailModal.scss';

interface Props {
  isOpen: boolean;
  closeModal: () => void;
  selectedId?: number;
}

type FormData = {
  notificationsEmail: string;
  notificationsContactName: string;
};

const EditNotificationsMailModal: React.FC<Props> = ({ isOpen, closeModal, selectedId }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const externalUserData = useSelector((state: RootState) => state.subcontractor.subcontractorNotificationEmail);
  const selectedProjectData = externalUserData?.find((project) => project.id === selectedId);
  const subcontractorId = useSelector((state: RootState) => state.user.userInfo?.userID);

  const validationSchema = yup.object().shape({
    notificationsEmail: yup.string().email(t('validation.email.invalidFormat')),
    notificationsContactName: yup.string()
  });

  const {
    handleSubmit,
    control,
    formState: { errors, isValid }
  } = useForm<FormData>({
    mode: 'onChange',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      notificationsEmail: selectedProjectData?.notificationsEmail,
      notificationsContactName: selectedProjectData?.notificationsContactName ?? ''
    }
  });

  const onSubmit = async (formData: FormData) => {
    if (selectedProjectData && subcontractorId && selectedProjectData.projectId) {
      const subcontractorData = {
        notificationsEmail: formData.notificationsEmail,
        projectId: selectedProjectData.projectId,
        notificationsContactName: formData.notificationsContactName
      };
      await dispatch(saveSubcontractorNotificationEmail(subcontractorData, subcontractorId));
      dispatch(getSubcontractorNotificationEmailSettings(subcontractorId));
      closeModal();
      dispatch(
        spawnGlobalNotification({
          type: GlobalNotificationTypes.Success,
          message: t('common.notification.success.message')
        })
      );
    }
  };

  const title = <h3 className="modal-title">{t('externalUsers.subcontractorRoles.editNotificationsMail')}</h3>;
  const buttons = (
    <div className="modal-buttons">
      <Button color={Color.Blue} buttonTypes={['transparent']} onClick={closeModal}>
        <span>{t('common.cancel')}</span>
      </Button>
      <Button disabled={!isValid} form="edit-notifications-mail" type="submit" buttonTypes={['blue']}>
        {t('common.confirm')}
      </Button>
    </div>
  );

  return (
    <Modal isOpen={isOpen} closeModal={closeModal} title={title} toolbar={buttons} size="small" height={275}>
      <form className="edit-notifications-mail-modal" id="edit-notifications-mail" onSubmit={handleSubmit(onSubmit)}>
        <div className="edit-notifications-mail-modal__content">
          <Controller
            name="notificationsContactName"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="string"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="notificationsContactName"
                error={errors.notificationsContactName?.message}
                name="notificationsContactName"
                label={`${t('common.contactPerson')} (${t('common.forNotifications')})`}
                placeholder={t('company.placeholder.enterFirstName')}
                value={value}
              />
            )}
          />
          <Controller
            name="notificationsEmail"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                type="string"
                inputMode="email"
                onChange={(e) => {
                  onChange(e.target.value);
                }}
                id="notificationsEmail"
                name="notificationsEmail"
                infoText={` (${t('common.forNotifications')})`}
                label={t('common.email')}
                placeholder={t('common.placeholder.email')}
                value={value}
                error={errors.notificationsEmail?.message}
              />
            )}
          />
        </div>
      </form>
    </Modal>
  );
};

export default EditNotificationsMailModal;
